import React, { useState, useMemo, useEffect } from "react";
import {
  User as UserIcon,
  Trophy as TrophyIcon,
  Clock as ClockIcon,
  BrainCircuit as BrainCircuitIcon,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import ModelBox from "./ModelBox";
import { resolveFriendlyName, resolveInternalKey } from '../shared/modelMap';
import { mergePersonalities } from '../utils/personalitiesLoader';

/**
 * ThreadMessages now accepts an optional `onAnalyze` prop:
 * onAnalyze = async (prompt, targetModel, exchangeObject) => { ... }
 */
const getFriendlyModelName = (id) => {
  const map = {
    'gpt-4o-mini': 'GPT-4o Mini',
    'gpt-4o': 'GPT-4o',
    'claude-opus-4-20250514': 'Claude Opus 4',
    'claude-3-sonnet': 'Claude 3 Sonnet',
    'gemini-2.5-flash': 'Gemini 2.5 Flash',
    'gemini-2.5-pro': 'Gemini 2.5 Pro',
  };
  return map[id] || id;
};

/**
 * Calculate statistics for a thread
 * Returns an object with round wins and tournament wins per agent
 */
const calculateThreadStats = (messages) => {
  const stats = {
    gpt: { roundWins: 0, tournamentWins: 0 },
    claude: { roundWins: 0, tournamentWins: 0 },
    gemini: { roundWins: 0, tournamentWins: 0 }
  };

  if (!messages || messages.length === 0) return stats;

  messages.forEach(exchange => {
    // Get winner from analysis
    const winner = exchange.analysis?.winner;
    if (!winner) return;

    // Normalize winner key to lowercase
    const winnerKey = String(winner).toLowerCase();

    // Count as round win for the winner
    if (stats[winnerKey]) {
      stats[winnerKey].roundWins++;

      // Check if this is a tournament win (2 models disqualified)
      const disqualifiedCount = (exchange.disqualifiedModels || []).length;
      if (disqualifiedCount === 2) {
        stats[winnerKey].tournamentWins++;
      }
    }
  });

  return stats;
};

const ExchangeItem = ({ exchange, index, total, onAnalyze, isInHistory, mergedPersonalities }) => {
  // Never auto-expand items in history section
  const isLatest = index === total - 1;
  const [isExpanded, setIsExpanded] = useState(false);
  const [notice, setNotice] = useState(null);

  // Safe guards
  const analysis = exchange.analysis ?? {};
  const stats = analysis.stats ?? {};
  const scores = analysis.scores ?? {};
  // If no analysis scores came through, recompute them from responses
if ((!scores.gpt && !scores.claude && !scores.gemini) && exchange.responses) {
  ['gpt', 'claude', 'gemini'].forEach((agent) => {
    const res = exchange.responses[agent];
    if (res?.success) {
      scores[agent] = {
        total: res.score?.total ?? 0,
        breakdown: res.score?.breakdown ?? {}
      };
    }
  });
}
  const winner = analysis.winner ?? exchange.winner ?? "—";
  const winnerScore =
    typeof analysis.winnerScore === "number" ? Math.round(analysis.winnerScore) : 0;
  const reasoning = analysis.reasoning ?? "";

// Updated getAvailableModelsForAnalysis:
const getAvailableModelsForAnalysis = () => {

  // Find successful responses
  const successfulModels = [];
  if (exchange.responses?.gpt?.success === true) successfulModels.push('gpt');
  if (exchange.responses?.claude?.success === true) successfulModels.push('claude');
  if (exchange.responses?.gemini?.success === true) successfulModels.push('gemini');

  // Filter out disqualified models (normalize to lowercase for comparison)
  const dqSet = new Set((exchange.disqualifiedModels || []).map(k => String(k).toLowerCase()));
  const filteredInternal = successfulModels.filter(modelKey => 
    !dqSet.has(modelKey.toLowerCase())
  );

  // Convert to friendly names for display
  const filteredFriendly = filteredInternal.map(resolveFriendlyName);
  
  return filteredInternal; // Return internal keys for logic, friendly names for display
};

const getModelInfo = (agent) => {
  const response = exchange?.responses?.[agent];

  // Use the model name directly from the response, which is already friendly
  const modelName = response?.model || `${agent.toUpperCase()} Model`;

  // Fix: pull personalityId from response
  const personalityId = response?.personalityId || 'default';

  // Use merged personalities (includes both built-in and custom)
  const personality = mergedPersonalities?.[agent]?.[personalityId];

  const subtitle = personality
    ? `${personality.name} - ${personality.description}`
    : 'Default';

  return {
    title: modelName,
    subtitle,
  };
};

  // Memoize model info so it updates when mergedPersonalities changes
  const gptInfo = useMemo(() => getModelInfo('gpt'), [exchange.responses?.gpt, mergedPersonalities]);
  const claudeInfo = useMemo(() => getModelInfo('claude'), [exchange.responses?.claude, mergedPersonalities]);
  const geminiInfo = useMemo(() => getModelInfo('gemini'), [exchange.responses?.gemini, mergedPersonalities]);

  const availableModels = useMemo(
    () => getAvailableModelsForAnalysis(),
    [exchange.responses, exchange.disqualifiedModels]
  );

  // Need at least 2 models alive to allow any analysis
  const canAnalyzeAny = availableModels.length >= 2;

  // Per-card flags based on internal keys, not friendly names
  const canAnalyzeGPT    = canAnalyzeAny && availableModels.includes('gpt');
  const canAnalyzeCLAUDE = canAnalyzeAny && availableModels.includes('claude');
  const canAnalyzeGEMINI = canAnalyzeAny && availableModels.includes('gemini');

  // Analyze handler (called by ModelBox via its onAnalyze prop)
  const handleAnalyze = async (targetModel) => {
    if (onAnalyze) {
      // Always extract original prompt from the first exchange in the thread
      let originalPrompt = exchange.originalPrompt;
      
      // If no originalPrompt stored, try to find it from the thread context
      if (!originalPrompt && window.currentThread) {
        const firstExchange = window.currentThread.messages[0];
        if (firstExchange && !firstExchange.prompt.startsWith('[ANALYSIS]')) {
          originalPrompt = firstExchange.prompt;
        }
      }
      
      // Fallback: extract from current prompt if it's an analysis
      if (!originalPrompt) {
        originalPrompt = exchange.prompt;
        if (originalPrompt.startsWith('[ANALYSIS]')) {
          const match = originalPrompt.match(/^\[ANALYSIS\]\s+[^:]+:\s*(.*)$/);
          if (match) {
            originalPrompt = match[1];
          }
        }
      }
      
      await onAnalyze(originalPrompt, targetModel, exchange);
    }
  };

// CLICK GUARD: recompute availability at click-time (no stale closures)
const handleAnalyzeGuard = (targetKey) => {
  const thread = window.currentThread;
  const latest = thread?.messages?.[thread.messages.length - 1] ?? exchange;

  // Current disqualified set (internal keys) - normalize to lowercase
  const dq = new Set((latest.disqualifiedModels || []).map(k => String(k).toLowerCase()));

  // Success map based on internal keys
  const successMap = {
    gpt: Boolean(latest.responses?.gpt?.success),
    claude: Boolean(latest.responses?.claude?.success),
    gemini: Boolean(latest.responses?.gemini?.success),
  };

  // Available = successful AND not disqualified
  const availableNow = Object.keys(successMap).filter(
    (k) => successMap[k] && !dq.has(k.toLowerCase())
  );

  // Only allow analysis on the latest exchange
  if (latest.id !== exchange.id) {
    setNotice({
      type: 'info',
      message: 'Analysis is only available for the most recent exchange.',
    });
    return;
  }

  // Normalize target key
  const normalizedTarget = String(targetKey).toLowerCase();

  // 1) Only one alive -> winner (no more analysis possible)
  if (availableNow.length <= 1) {
    const winnerKey = availableNow[0] || normalizedTarget;
    setNotice({
      type: 'winner',
      message: `🏆 ${resolveFriendlyName(winnerKey)} is the final winner! No opponents left to analyze.`,
    });
    return;
  }

  // 2) Target already disqualified
  if (!availableNow.includes(normalizedTarget)) {
    setNotice({
      type: 'disqualified',
      message: `⚠️ ${resolveFriendlyName(normalizedTarget)} has already been disqualified in this round.`,
    });
    return;
  }

  // 3) OK, proceed with analysis
  setNotice(null);
  handleAnalyze(resolveFriendlyName(normalizedTarget)); // Pass friendly name to the handler
};

  return (
    <div
      className={`space-y-4 ${!isExpanded ? "opacity-90" : ""}`}
      key={exchange.id}
    >
      {/* Collapsible header - ALL items in history are collapsed */}
      {isInHistory && (
        <button
          onClick={() => setIsExpanded((v) => !v)}
          className="w-full text-left p-4 glass border border-slate-700/30 rounded-2xl hover:border-slate-600/50 transition-all shadow-lg"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-slate-200 font-semibold text-sm sm:text-base">
              Round {index + 1}: "{(exchange.prompt || "").substring(0, 50)}
              {exchange.prompt && exchange.prompt.length > 50 ? "..." : ""}"
            </span>
            <div className="flex items-center gap-3">
              <span className="text-yellow-300 text-sm font-bold">
                {(exchange.disqualifiedModels || []).length === 2 ? 'Tournament Winner' : 'Winner'}: {exchange.responses?.[String(winner || '').toLowerCase()]?.model || winner || '—'}
              </span>
              <ChevronDownIcon
                className={`h-5 w-5 text-cyan-400 transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </button>
      )}

      {/* Full content – only show if expanded (or if not in history) */}
      {(isExpanded || !isInHistory) && (
        <>
          {/* User Message - Enhanced */}
          <div className="flex items-start gap-4">
            <div className="glass p-3 rounded-xl border border-slate-700/50 shadow-lg">
              <UserIcon className="h-5 w-5 text-cyan-400" />
            </div>
            <div className="flex-1 glass rounded-2xl p-5 border border-slate-700/50 shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-cyan-300">You</span>
                <span className="text-xs text-slate-400 font-medium">
                  {exchange.timestamp ? new Date(exchange.timestamp).toLocaleString() : ""}
                </span>
              </div>
              <p className="text-white font-medium leading-relaxed">{exchange.prompt}</p>
            </div>
          </div>

          {/* Winner Announcement - Enhanced */}
          <div className="glass border border-yellow-500/30 rounded-2xl p-5 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <TrophyIcon className="h-6 w-6 text-yellow-400" />
                <div>
                  <span className="text-yellow-300 font-bold text-sm">
                    {(exchange.disqualifiedModels || []).length === 2 ? 'Tournament Winner' : `Round ${index + 1} Winner`}
                  </span>
                  <p className="text-white font-black text-lg">
                    {exchange.responses?.[String(winner || '').toLowerCase()]?.model || winner || '—'}
                  </p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <div className="text-cyan-300 font-bold">Score: {winnerScore}/100</div>
                {reasoning && <div className="text-slate-400 text-xs mt-1 line-clamp-2">{reasoning}</div>}
              </div>
            </div>
          </div>

          {/* AI Responses - Enhanced Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ml-0 sm:ml-8">
            <ModelBox
              agentKey="gpt"
              title={gptInfo.title}
              subtitle={gptInfo.subtitle}
              result={exchange.responses?.gpt}
              isWinner={winner === "GPT"}
              score={scores?.gpt}
              color="emerald"
              canAnalyze={isLatest && canAnalyzeGPT}
              onAnalyze={isLatest && canAnalyzeGPT ? () => handleAnalyzeGuard("gpt") : null}
              isDisqualified={(() => {
                const dqList = exchange.disqualifiedModels || [];
                const isInDQList = dqList.some(m => String(m).toLowerCase() === 'gpt');

                // WORKAROUND: Also check if response failed with "Disqualified from analysis" message
                const response = exchange.responses?.gpt;
                const isDisqualifiedByContent = response &&
                  !response.success &&
                  response.content &&
                  String(response.content).toLowerCase().includes('disqualified');

                const isGPTDisqualified = isInDQList || isDisqualifiedByContent;
                return isGPTDisqualified;
              })()}
            />
            <ModelBox
              agentKey="claude"
              title={claudeInfo.title}
              subtitle={claudeInfo.subtitle}
              result={exchange.responses?.claude}
              isWinner={winner === "CLAUDE"}
              score={scores?.claude}
              color="blue"
              canAnalyze={isLatest && canAnalyzeCLAUDE}
              onAnalyze={isLatest && canAnalyzeCLAUDE ? () => handleAnalyzeGuard("claude") : null}
              isDisqualified={(() => {
                const dqList = exchange.disqualifiedModels || [];
                const isInDQList = dqList.some(m => String(m).toLowerCase() === 'claude');

                // WORKAROUND: Also check if response failed with "Disqualified from analysis" message
                const response = exchange.responses?.claude;
                const isDisqualifiedByContent = response &&
                  !response.success &&
                  response.content &&
                  String(response.content).toLowerCase().includes('disqualified');

                const isClaudeDisqualified = isInDQList || isDisqualifiedByContent;
                return isClaudeDisqualified;
              })()}
            />
            <ModelBox
              agentKey="gemini"
              title={geminiInfo.title}
              subtitle={geminiInfo.subtitle}
              result={exchange.responses?.gemini}
              isWinner={winner === "GEMINI"}
              score={scores?.gemini}
              color="purple"
              canAnalyze={isLatest && canAnalyzeGEMINI}
              onAnalyze={isLatest && canAnalyzeGEMINI ? () => handleAnalyzeGuard("gemini") : null}
              isDisqualified={(() => {
                const dqList = exchange.disqualifiedModels || [];
                const isInDQList = dqList.some(m => String(m).toLowerCase() === 'gemini');

                // WORKAROUND: Also check if response failed with "Disqualified from analysis" message
                const response = exchange.responses?.gemini;
                const isDisqualifiedByContent = response &&
                  !response.success &&
                  response.content &&
                  String(response.content).toLowerCase().includes('disqualified');

                const isGeminiDisqualified = isInDQList || isDisqualifiedByContent;
                return isGeminiDisqualified;
              })()}
            />
          </div>

          {/* Notice banner - Enhanced */}
          {notice && (
            <div
              className={`glass rounded-2xl p-5 ml-0 sm:ml-8 mb-4 border shadow-lg ${
                notice.type === "winner"
                  ? "border-green-500/30"
                  : "border-yellow-500/30"
              }`}
            >
              <div className="flex items-center justify-center text-center">
                <span
                  className={`font-bold text-base ${
                    notice.type === "winner" ? "text-green-300" : "text-yellow-300"
                  }`}
                >
                  {notice.message}
                </span>
              </div>
            </div>
          )}

          {/* Exchange Stats - Enhanced */}
          <div className="glass border border-slate-700/30 rounded-2xl p-5 ml-0 sm:ml-8 shadow-lg">
            <h4 className="text-base font-bold text-slate-200 mb-4 flex items-center gap-2">
              <BrainCircuitIcon className="h-5 w-5 text-cyan-400" />
              Round {index + 1} Statistics
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="glass rounded-xl p-4 border border-slate-700/30">
                <p className="text-slate-400 text-sm font-semibold mb-2">Successful Responses</p>
                <p className="text-white font-black text-2xl">{stats?.totalResponses ?? 0}/3</p>
              </div>
              <div className="glass rounded-xl p-4 border border-slate-700/30">
                <p className="text-slate-400 text-sm font-semibold mb-2">Avg Response Time</p>
                <p className="text-cyan-400 font-black text-2xl">
                  {stats?.averageResponseTime
                    ? (stats.averageResponseTime / 1000).toFixed(1)
                    : "—"}
                  s
                </p>
              </div>
              <div className="glass rounded-xl p-4 border border-slate-700/30">
                <p className="text-slate-400 text-sm font-semibold mb-2">Total Tokens</p>
                <p className="text-purple-400 font-black text-2xl">
                  {(stats?.totalTokens ?? 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          {!isLatest && <div className="border-t border-slate-700/50 my-8" />}
        </>
      )}
    </div>
  );
};

const ThreadMessages = ({ thread, loading, onAnalyze }) => {
  const [customPersonas, setCustomPersonas] = useState([]);

  // Load custom personas on mount
  useEffect(() => {
    const token = localStorage.getItem("neuroforge_token");
    const loadCustomPersonas = async () => {
      try {
        const response = await fetch('/api/custom-personas', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCustomPersonas(data.personas || []);
        }
      } catch (error) {
        console.error('[ThreadMessages] Failed to load custom personas:', error);
      }
    };

    if (token) {
      loadCustomPersonas();
    }
  }, []);

  // Merge custom personas with built-in personalities for each agent
  const mergedPersonalities = useMemo(() => ({
    gpt: mergePersonalities(customPersonas, 'gpt'),
    claude: mergePersonalities(customPersonas, 'claude'),
    gemini: mergePersonalities(customPersonas, 'gemini')
  }), [customPersonas]);

  // Calculate thread statistics
  const threadStats = useMemo(() => {
    return calculateThreadStats(thread?.messages || []);
  }, [thread?.messages]);

  // Make thread available globally for debugging
  React.useEffect(() => {
    window.currentThread = thread;
  }, [thread]);

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <BrainCircuitIcon className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Select a thread or start a new conversation</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading thread...</p>
        </div>
      </div>
    );
  }

  // Separate current battle from history
  const currentBattle = thread.messages.length > 0 ? thread.messages[thread.messages.length - 1] : null;
  const historyMessages = thread.messages.length > 1 ? thread.messages.slice(0, -1).reverse() : [];

  return (
    <div className="space-y-6">
      {/* Thread Header & Statistics - Combined */}
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl space-y-5">
        {/* Thread Info */}
        <div>
          <h2 className="text-2xl font-black text-white mb-3">{thread.title}</h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-400">
            <span className="flex items-center gap-2 font-semibold">
              <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
              {thread.messages.length} exchange{thread.messages.length !== 1 ? "s" : ""}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="font-medium">Started {new Date(thread.createdAt).toLocaleDateString()}</span>
            <span className="hidden sm:inline">•</span>
            <span className="font-medium">Updated {new Date(thread.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Thread Statistics */}
        {thread.messages.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrophyIcon className="h-5 w-5 text-yellow-400" />
              <h3 className="text-lg font-bold text-white">Thread Statistics</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* GPT Stats */}
              <div className="glass border border-slate-600/30 rounded-xl p-4">
                <div className="text-sm font-bold text-cyan-300 mb-2">GPT</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Round Wins:</span>
                    <span className="text-white font-bold">{threadStats.gpt.roundWins}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Tournament Wins:</span>
                    <span className="text-yellow-400 font-bold">{threadStats.gpt.tournamentWins}</span>
                  </div>
                </div>
              </div>

              {/* Claude Stats */}
              <div className="glass border border-slate-600/30 rounded-xl p-4">
                <div className="text-sm font-bold text-orange-300 mb-2">Claude</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Round Wins:</span>
                    <span className="text-white font-bold">{threadStats.claude.roundWins}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Tournament Wins:</span>
                    <span className="text-yellow-400 font-bold">{threadStats.claude.tournamentWins}</span>
                  </div>
                </div>
              </div>

              {/* Gemini Stats */}
              <div className="glass border border-slate-600/30 rounded-xl p-4">
                <div className="text-sm font-bold text-blue-300 mb-2">Gemini</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Round Wins:</span>
                    <span className="text-white font-bold">{threadStats.gemini.roundWins}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Tournament Wins:</span>
                    <span className="text-yellow-400 font-bold">{threadStats.gemini.tournamentWins}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Battle Results */}
      {currentBattle && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
            <div className="glass border border-cyan-500/50 rounded-xl px-5 py-2 shadow-lg">
              <span className="text-cyan-300 font-black text-sm uppercase tracking-wide flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                Current Battle
              </span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          </div>

          <ExchangeItem
            key={`current-${currentBattle.id}`}
            exchange={currentBattle}
            index={thread.messages.length - 1}
            total={thread.messages.length}
            onAnalyze={onAnalyze}
            isInHistory={false}
            mergedPersonalities={mergedPersonalities}
          />
        </div>
      )}

      {/* History Section */}
      {historyMessages.length > 0 && (
        <div className="space-y-6 mt-12">
          {/* History Header with stunning separator */}
          <div className="relative py-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-600/50 to-transparent"></div>
            </div>
            <div className="relative flex justify-center">
              <div className="glass border border-slate-600/50 rounded-2xl px-8 py-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <ClockIcon className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-300 font-black text-lg uppercase tracking-wide">
                    Battle History
                  </span>
                  <div className="glass border border-slate-600/30 rounded-lg px-3 py-1">
                    <span className="text-slate-400 font-bold text-sm">
                      {historyMessages.length} Previous Round{historyMessages.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Historical exchanges - all collapsed */}
          <div className="space-y-4">
            {historyMessages.map((exchange, historyIndex) => {
              // Calculate the original index (since we reversed the array)
              const originalIndex = thread.messages.length - 2 - historyIndex;
              const dqKey = (exchange.disqualifiedModels || []).slice().sort().join('|');
              const successKey = ['gpt','claude','gemini']
                .map(k => (exchange.responses?.[k]?.success ? '1' : '0'))
                .join('');

              return (
                <ExchangeItem
                  key={`history-${exchange.id ?? originalIndex}-${dqKey}-${successKey}`}
                  exchange={exchange}
                  index={originalIndex}
                  total={thread.messages.length}
                  onAnalyze={onAnalyze}
                  isInHistory={true}
                  mergedPersonalities={mergedPersonalities}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state for new threads */}
      {thread.messages.length === 0 && (
        <div className="text-center py-12">
          <BrainCircuitIcon className="h-16 w-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Ready for Battle</h3>
          <p className="text-slate-400 mb-4">
            This thread is empty. Send your first message to start the AI battle!
          </p>
          <p className="text-slate-500 text-sm">
            All three AI models will respond with their unique personalities and compete
            for the best answer.
          </p>
        </div>
      )}
    </div>
  );
};

export default ThreadMessages;
